import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { removeNodeIds } from "@/models/utils/removeNodeIds";
import { isSupSubNode } from "@/models/utils/semanticChecks";

import type { CapturesMap } from "./types";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeJson } from "@corca/data";

type Options = {
  resetIds?: boolean;
};

/**
 * Walks the target variant's pattern, replacing placeholders with captured nodes.
 * Concrete nodes are preserved with their type and props; children are recursed.
 *
 * Grouping is applied here (not stored in the notation JSON):
 * - **Wrap:** when a captured operand of more than one atomic operand (e.g. `x+y`) is
 *   substituted into a level-1 placeholder that has siblings (the flat form), it is wrapped in
 *   a `(…)` group so precedence is preserved — `(x+y)/(z+a)`. A single node or a single
 *   base+supsub (`x²`) is one atomic operand and is left bare.
 * - **Unwrap:** when the captured operand is a single regular-parenthesis group `(…)` landing
 *   alone in an inline (e.g. a fraction numerator), the parentheses are stripped — the container
 *   already delimits it. Groups with other delimiters (`|…|`, `[…]`, …) are left intact, since
 *   those carry meaning.
 *
 * @returns New `NodeJson[]` with captures substituted in, or `null` if empty.
 */
export function substituteCaptures(
  variant: NodeJson[],
  captures: CapturesMap,
  { resetIds = true }: Options = {},
): NodeJson[] | null {
  const substitute = (pattern: NodeJson[], level = 0): NodeJson[] => {
    const isAlone = pattern.length === 1;

    return pattern.flatMap((patternNode, index) => {
      if (patternNode.type === "placeholder") {
        const { linkId } = patternNode.props;

        if (!linkId || !captures.has(linkId)) {
          return [patternNode];
        }

        const capture = captures.get(linkId)!;

        if (capture.length === 0 && level === 0) {
          return [
            {
              type: "placeholder",
              props: {},
              children: [{ type: "inline", props: {}, children: [] }],
            },
          ];
        }

        if (isAlone && level > 0 && isSingleParenGroup(capture)) {
          return (capture[0] as GroupNodeModel).inlineNode!.nodes.map((node) =>
            resetIds ? removeNodeIds(node) : node.toJSON(),
          );
        }

        const nodesJson = capture.map((node) => (resetIds ? removeNodeIds(node) : node.toJSON()));

        if (needsGroup(capture, pattern[index + 1], isAlone)) {
          return [groupJson(nodesJson)];
        }

        return nodesJson;
      }

      return [
        {
          ...patternNode,
          children: substitute(patternNode.children, level + 1),
        },
      ];
    });
  };

  const substituted = substitute(variant);

  if (substituted.length > 0) {
    return substituted;
  }

  return null;
}

function needsGroup(
  capture: NodeModel[],
  nextPatternNode: NodeJson | undefined,
  isAlone: boolean,
): boolean {
  if (isSupSubNode(nextPatternNode)) {
    return capture.length > 1;
  }

  return !isAlone && operandCount(capture) > 1;
}

function operandCount(capture: NodeModel[]): number {
  return capture.filter((node) => !isSupSubNode(node)).length;
}

function isSingleParenGroup(capture: NodeModel[]): boolean {
  const [node] = capture;

  return (
    capture.length === 1 &&
    node.is(GroupNodeModel) &&
    !!node.inlineNode &&
    node.openingSymbol === "(" &&
    node.closingSymbol === ")"
  );
}

function groupJson(children: NodeJson[]): NodeJson {
  return {
    type: "group",
    props: { openingSymbol: "(", closingSymbol: ")" },
    children: [
      {
        type: "matrix",
        props: { rows: 1, cols: 1 },
        children: [{ type: "inline", props: {}, children }],
      },
    ],
  };
}
